import { useCallback, useEffect, useRef, useState } from "react"
import { getUnreadThreadCount } from "src/apis/contact/getUnreadThreadCount"
import { Buckets } from "src/aws/customAPI"
import { ContactOverviewModel } from "src/types/contact/ContactOverviewModel"
import { UnreadReplyCountCacheService } from "src/services/cache/UnreadReplyCountCacheService"
import { OptimisticStateService } from "src/services/cache/OptimisticStateService"
import { useRecoilValue } from "recoil"
import { forceRefreshUnreadCountState } from "src/recoil/atoms/contact/forceRefreshUnreadCountState"

type UnreadCountMap = Map<string, number>

interface UseIncrementalUnreadCountParams {
  contacts?: {
    contacts: ContactOverviewModel[]
    nextToken?: string
    total?: number | null
  }
  request: {
    accessToken: string
    accountId: string
  } | null
}

/**
 * 増分的に未読数を取得・管理するフック
 *
 * ページング時に新しい連絡のみの未読数を取得し、
 * 既存のデータとマージすることでOpenSearchの制限を回避します
 */
export const useIncrementalUnreadCount = ({ contacts, request }: UseIncrementalUnreadCountParams) => {
  // 強制リフレッシュトリガー
  const forceRefreshTrigger = useRecoilValue(forceRefreshUnreadCountState)

  // 未読カウントをMapで管理（高速な検索のため）
  const [unreadCountMap, setUnreadCountMap] = useState<UnreadCountMap>(new Map())

  // Buckets形式のデータ（既存コンポーネントとの互換性のため）
  const [unreadPerContact, setUnreadPerContact] = useState<Buckets[]>([])

  // 前回処理した連絡IDを保持
  const processedContactIds = useRef<Set<string>>(new Set())

  // contacts と request を ref で保持（setTimeout等から最新の値を参照するため）
  const contactsRef = useRef(contacts)
  const requestRef = useRef(request)
  useEffect(() => {
    contactsRef.current = contacts
  }, [contacts])
  useEffect(() => {
    requestRef.current = request
  }, [request])

  // contactsが変わるたびにキャッシュから楽観的既読を復元
  useEffect(() => {
    if (!contacts?.contacts || contacts.contacts.length === 0) return

    // OptimisticStateServiceを使用してキャッシュから楽観的既読を復元
    const contactIds = contacts.contacts.map((contact) => contact.id)
    OptimisticStateService.restoreOptimisticallyReadFromCache(contactIds)
  }, [contacts?.contacts])

  // 連絡リストがリセットされた場合の処理
  const resetUnreadCounts = useCallback(() => {
    processedContactIds.current = new Set()
    // OptimisticStateServiceの楽観的既読もクリア
    OptimisticStateService.clearOptimisticallyRead()
    setUnreadCountMap(new Map())
    setUnreadPerContact([])
  }, [])

  // フィルター/検索変更時の処理（楽観的既読は保持）
  const resetProcessedContactsOnly = useCallback(() => {
    console.log("[useIncrementalUnreadCount] resetProcessedContactsOnly 実行", OptimisticStateService.getDebugInfo())

    // processedContactIdsのみクリア（楽観的既読は保持）
    processedContactIds.current = new Set()

    // unreadCountMapとunreadPerContactはクリアせず、そのまま保持
    // 次のuseEffectで新しい連絡の未読数を取得する際に、楽観的既読が適用される
  }, [])

  // 強制的に未読数を再取得（画面フォーカス時など）
  // 注意: この関数はsetTimeout等から呼び出されることがあるため、
  // contacts/requestはrefを使用して常に最新の値を参照する
  const forceRefreshUnreadCounts = useCallback(() => {
    const currentContacts = contactsRef.current
    const currentRequest = requestRef.current

    if (!currentRequest || !currentContacts?.contacts || currentContacts.contacts.length === 0) {
      console.log("[useIncrementalUnreadCount] forceRefresh スキップ: request or contacts が空")
      return
    }

    // 全ての連絡IDで未読数を再取得
    const allContactIds = currentContacts.contacts.map((contact) => contact.id)
    console.log("[useIncrementalUnreadCount] forceRefresh 開始:", { contactCount: allContactIds.length })

    getUnreadThreadCount({
      ...currentRequest,
      contactIds: allContactIds,
    })
      .then((data) => {
        const debugInfo = OptimisticStateService.getDebugInfo()
        console.log("[useIncrementalUnreadCount] forceRefresh レスポンス:", {
          hasBuckets: !!data?.buckets,
          bucketCount: data?.buckets?.length,
          optimisticallyReadCount: debugInfo.optimisticallyReadCount,
        })

        if (!data?.buckets) {
          // データがない場合は空の状態にする
          console.log("[useIncrementalUnreadCount] forceRefresh: データなし、空にする")
          setUnreadCountMap(new Map())
          setUnreadPerContact([])
          processedContactIds.current = new Set(allContactIds)
          return
        }

        // 完全に置き換える
        const newMap = new Map<string, number>()

        // まず、全ての連絡を0で初期化してキャッシュに保存
        // DynamoDB実装では未読数0の連絡がレスポンスに含まれないため、明示的に0を設定する必要がある
        allContactIds.forEach((contactId) => {
          newMap.set(contactId, 0)
          // 0もキャッシュに保存（新規作成した連絡など、未読数0のものもバッジ更新のため必要）
          UnreadReplyCountCacheService.set(contactId, 0)
        })

        // 次に、APIレスポンスの値で上書き
        data.buckets.forEach((bucket: Buckets) => {
          // OptimisticStateServiceを使用して楽観的既読をチェック
          if (OptimisticStateService.isOptimisticallyRead(bucket.key)) {
            console.log("[useIncrementalUnreadCount] 楽観的既読を維持:", { contactId: bucket.key })
            // 0のまま（上書きしない）
            UnreadReplyCountCacheService.set(bucket.key, 0)
          } else {
            newMap.set(bucket.key, bucket.doc_count)
            // キャッシュにも保存（コールバック経由でstateが更新される）
            UnreadReplyCountCacheService.set(bucket.key, bucket.doc_count)
          }
        })

        console.log("[useIncrementalUnreadCount] forceRefresh 完了:", { unreadCount: newMap.size })
        setUnreadCountMap(newMap)

        // unreadPerContact も楽観的既読を反映
        const updatedBuckets = data.buckets.map((bucket: Buckets) =>
          OptimisticStateService.isOptimisticallyRead(bucket.key) ? { ...bucket, doc_count: 0 } : bucket
        )
        setUnreadPerContact(updatedBuckets)

        // 処理済みIDを更新
        processedContactIds.current = new Set(allContactIds)
      })
      .catch((error) => {
        console.error("[useIncrementalUnreadCount] 未読数の取得に失敗しました:", error)
      })
  }, []) // contacts/requestはrefを使用するため依存配列から除外

  useEffect(() => {
    if (!request || !contacts?.contacts || contacts.contacts.length === 0) {
      return
    }

    // 新しい連絡IDを抽出
    const newContactIds = contacts.contacts.map((contact) => contact.id).filter((id) => !processedContactIds.current.has(id))

    if (newContactIds.length === 0) {
      return
    }

    // 新しい連絡の未読数を取得
    getUnreadThreadCount({
      ...request,
      contactIds: newContactIds,
    })
      .then((data) => {
        if (!data?.buckets) return

        // 新しいデータをMapにマージ
        setUnreadCountMap((prevMap) => {
          const newMap = new Map(prevMap)
          data.buckets.forEach((bucket: Buckets) => {
            // OptimisticStateServiceを使用して楽観的既読をチェック
            const isOptimisticallyRead = OptimisticStateService.isOptimisticallyRead(bucket.key)

            if (isOptimisticallyRead) {
              newMap.set(bucket.key, 0)
            } else {
              newMap.set(bucket.key, bucket.doc_count)
              // キャッシュにも保存（コールバック経由でstateが更新される）
              UnreadReplyCountCacheService.set(bucket.key, bucket.doc_count)
            }
          })
          return newMap
        })

        // 処理済みIDを更新
        newContactIds.forEach((id) => {
          processedContactIds.current.add(id)
        })

        // Buckets形式のデータも更新（既存コンポーネントとの互換性のため）
        setUnreadPerContact((prevBuckets) => {
          // 既存のバケットをMapに変換
          const bucketsMap = new Map(prevBuckets.map((b) => [b.key, b]))

          // 新しいデータで更新（楽観的既読を反映）
          data.buckets.forEach((bucket: Buckets) => {
            const isOptimisticallyRead = OptimisticStateService.isOptimisticallyRead(bucket.key)
            bucketsMap.set(bucket.key, isOptimisticallyRead ? { ...bucket, doc_count: 0 } : bucket)
          })

          return Array.from(bucketsMap.values())
        })
      })
      .catch((error) => {
        console.error("[useIncrementalUnreadCount] 未読数の取得に失敗しました:", error)
      })
  }, [request, contacts])

  // 連絡リストがクリアされた場合（例：フィルタ変更、検索）
  // 注意: 楽観的既読状態(OptimisticStateService)はクリアしない
  // これにより、リフレッシュ時に一時的に連絡リストが空になっても、
  // 以前既読にした連絡のバッジが再表示されることを防ぐ
  useEffect(() => {
    if (!contacts?.contacts || contacts.contacts.length === 0) {
      // processedContactIdsのみクリア（楽観的既読は保持）
      processedContactIds.current = new Set()
      setUnreadCountMap(new Map())
      setUnreadPerContact([])
    }
  }, [contacts?.contacts])

  // 強制リフレッシュトリガーの監視
  useEffect(() => {
    if (forceRefreshTrigger > 0) {
      console.log("[useIncrementalUnreadCount] 強制リフレッシュがトリガーされました:", forceRefreshTrigger)
      // processedContactIdsをクリアして全ての連絡の未読数を再取得
      processedContactIds.current = new Set()
      // 明示的に再取得を実行
      forceRefreshUnreadCounts()
    }
  }, [forceRefreshTrigger, forceRefreshUnreadCounts])

  // UnreadReplyCountCacheServiceからのキャッシュ更新を受け取る
  useEffect(() => {
    const callback = (contactId: string, newCount: number) => {
      console.log("[useIncrementalUnreadCount] キャッシュ更新を受信:", { contactId, newCount })

      // 未読数が0の場合、楽観的既読に追加（連絡詳細画面で既読にした場合）
      if (newCount === 0) {
        OptimisticStateService.markAsRead(contactId)
      }

      // stateを更新して画面を再レンダリング
      setUnreadCountMap((prevMap) => {
        const newMap = new Map(prevMap)
        newMap.set(contactId, newCount)
        return newMap
      })
      setUnreadPerContact((prevBuckets) => {
        // 既存のバケットを更新、または新規追加
        const existingIndex = prevBuckets.findIndex((b) => b.key === contactId)
        if (existingIndex >= 0) {
          const updated = [...prevBuckets]
          updated[existingIndex] = { ...updated[existingIndex], doc_count: newCount }
          return updated
        } else {
          return [...prevBuckets, { key: contactId, doc_count: newCount }]
        }
      })
    }

    // コールバックを登録
    UnreadReplyCountCacheService.addUpdateCallback(callback)

    // クリーンアップ
    return () => {
      UnreadReplyCountCacheService.removeUpdateCallback(callback)
    }
  }, [])

  // 特定の連絡の未読数を0にする（楽観的更新）
  const markContactAsRead = useCallback((contactId: string) => {
    console.log("[useIncrementalUnreadCount] markContactAsRead:", { contactId })
    // 非同期で実行してレンダリング中のstate更新を回避
    setTimeout(() => {
      // OptimisticStateServiceに楽観的既読を追加
      OptimisticStateService.markAsRead(contactId)
      setUnreadCountMap((prevMap) => {
        const newMap = new Map(prevMap)
        newMap.set(contactId, 0)
        return newMap
      })
      setUnreadPerContact((prevBuckets) => {
        return prevBuckets.map((bucket) => (bucket.key === contactId ? { ...bucket, doc_count: 0 } : bucket))
      })
    }, 0)
  }, [])

  return {
    unreadPerContact,
    unreadCountMap,
    resetUnreadCounts,
    resetProcessedContactsOnly,
    forceRefreshUnreadCounts,
    markContactAsRead,
    getUnreadCount: useCallback(
      (contactId: string) => {
        return unreadCountMap.get(contactId) || 0
      },
      [unreadCountMap]
    ),
  }
}
